import * as React from 'react';
import {
  AlertColor,
  Grid,
  CardContent,
  TextField,
  Typography,
  IconButton,
  DialogContent,
  DialogActions,
  TableHead,
  TableRow,
  TableCell,
  styled,
  Table,
  TableBody,
  useTheme,
} from '@mui/material';
import { Formik, FormikErrors } from 'formik';
import {
  useDisableMFAMutation,
  useEnableMFAMutation,
  useGetLoginHistoryQuery,
  useLazyGetCurrentUserQuery,
  useUpdateUserPasswordMutation,
  useVerifyMFAMutation,
} from 'src/redux/services/users';
import { PartialUser, UpdateUserPasswordPayload } from 'src/dto/User';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { SerializedError } from '@reduxjs/toolkit';
import SnackBarCustom from 'src/frontend-common/components/SnackbarCustom';
import CloseIcon from '@mui/icons-material/Close';
import ButtonComponent from 'src/components/ButtonComponent';
import { CardTitleContainer } from '../BillingInfo';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { useInitialPrefix } from 'src/hooks';
import { PhoneErrorWrapper } from 'src/pages/UserComplete/components/CompleteUserForm';
import { CheckCircle, Error } from '@mui/icons-material';
import { ActionsWrapper, ContainerCard, DialogWrapper, FormContainer, TypographyGridTitle, WhiteContainer } from 'src/style/common';

import IOSSwitch from 'src/components/IosSwitch';
import EmptyState from 'src/frontend-common/components/EmptyState';

interface ChangePasswordData {
  data: {
    new_password: string;
    previous_password: string;
  };
}
interface ChangePasswordError {
  error: FetchBaseQueryError | SerializedError;
}
type UpdatePasswordResponse = ChangePasswordData | ChangePasswordError;

const hasError = (p: UpdatePasswordResponse): p is ChangePasswordError => {
  return (p as ChangePasswordError).error !== undefined;
};

interface FormValues {
  newPassword: string;
  oldPassword: string;
  newPasswordConf: string;
}

interface SnackBarStates {
  open: boolean;
  message: string;
  severity: AlertColor;
}

const defaultSnackBarState: SnackBarStates = {
  open: false,
  message: '',
  severity: 'success',
};

const SecondWhiteContainer = styled(WhiteContainer)(({ theme }) => ({
  marginTop: theme.spacing(4),
  boxShadow: '0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)',
  padding: theme.spacing(0),
}));

const TableCustomSize = styled(Table)(() => ({
  '& th, td': {
    width: '40%',
    '&:last-of-type': {
      width: '20%',
    },
  },
}));

const FirstBlockContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
}));

export interface SecurityTabProps {
  user: PartialUser | null;
}

const SecurityTab: React.FC<SecurityTabProps> = ({ user }) => {
  const [snackBar, setSnackBar] = React.useState<SnackBarStates>(defaultSnackBarState);
  const [updateUserPassword, { isLoading: updateUserPasswordIsLoading }] = useUpdateUserPasswordMutation();
  const [open, setOpen] = React.useState(false);

  const [verifyNumber, setVerifyNumber] = React.useState<boolean>(false);
  const [isRateLimitError, setIsRateLimitError] = React.useState<boolean>(false);
  const [isResend, setIsResend] = React.useState<boolean>(false);
  const [disableResend, setDisableResend] = React.useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = React.useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = React.useState<string>(user?.phone?.replace('+', '') || '');
  const [countryCode, setCountryCode] = React.useState<string>(user?.phone_country_code ? user?.phone_country_code.replace('+', '') : '');
  const [timeToReset, setTimeToReset] = React.useState<string>();
  const [idToFocus, setIdToFocus] = React.useState<number>(0);
  const [smsCode, setSmsCode] = React.useState<string[]>([]);
  const [enableMFA, { reset: resetEnable, isLoading: loadingEnableMFA, isError: isErrorEnableMFA, error: errorEnableMFA, isSuccess: isSuccessEnableMFA }] =
    useEnableMFAMutation();
  const [triggerGetUser] = useLazyGetCurrentUserQuery();

  const { data: loginUserInfoData } = useGetLoginHistoryQuery({
    username: user?.email || '',
  });
  const [disableMFA] = useDisableMFAMutation();
  const [verifyMFA, { isLoading: loadingMFAVerify, isError: isErrorMFAVerify, isSuccess: isSuccessMFAVerify, reset: resetVerify }] = useVerifyMFAMutation();

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    if (!user?.phone) {
      setPhoneNumber('');

      setCountryCode('');
    }
    setIsResend(false);
    setVerifyNumber(false);
    setShowSuccessMessage(false);
    setIsRateLimitError(false);
    resetEnable();
    resetVerify();
  };
  const isValidPhone = phoneNumber?.match('^[+]?[(]?[0-9]{3}[)]?[-s.]?[0-9]{3}[-s.]?[0-9]{4,6}$');

  const theme = useTheme();

  React.useEffect(() => {
    if (errorEnableMFA) {
      if ('data' in errorEnableMFA) {
        // @ts-ignore
        const dataError = errorEnableMFA.data.error;
        if (dataError && dataError === 'rate_limit') {
          setIsRateLimitError(true);
        }
        if (errorEnableMFA.data) {
          // @ts-ignore
          const next_request = errorEnableMFA.data.next_request;
          if (next_request) {
            const secsToResend = Math.floor(Math.abs(Date.now() / 1000 - next_request));
            let resendtimer = `${secsToResend} seconds`;

            if (secsToResend > 60) {
              resendtimer = `${Math.floor(secsToResend / 60)} minutes`;
            }

            setTimeToReset(resendtimer);
            setTimeout(() => setIsRateLimitError(false), secsToResend * 1000);
          }
        }
      }
    }
    if (isSuccessMFAVerify) setShowSuccessMessage(true);
  }, [errorEnableMFA, isSuccessMFAVerify]);
  const initialPrefix = useInitialPrefix();
  const securityContent = (
    <>
      <SnackBarCustom open={snackBar.open} onClose={() => setSnackBar({ ...snackBar, open: false })} message={snackBar.message} severity={snackBar.severity} />
      {user?.provider === 'PROVIDER_COGNITO' && (
        <>
          <CardTitleContainer
            style={{
              marginTop: 24,
              paddingTop: 32,
              paddingBottom: 32,
              paddingLeft: 24,
              paddingRight: 24,
            }}
          >
            <div style={{ float: 'left', width: '50%', display: 'inline-block' }}>
              <Typography variant="h5">Two-Factor Authentication</Typography>
              <Typography variant="body2">Protect your account by adding another layer of security.</Typography>
            </div>
            <div style={{ float: 'right' }}>
              <IOSSwitch color="primary" onClick={handleOpen} checked={user?.mfa_list?.length !== 0} />
            </div>
          </CardTitleContainer>
          <DialogWrapper onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description" open={open}>
            <div
              style={{
                backgroundColor: theme.palette.primary.main,
                height: '78px',
                alignItems: 'center',
                display: 'flex',
              }}
            >
              <Typography sx={{ m: 0, p: 2, color: 'white' }} variant="h5">
                Two-Factor Authentication
              </Typography>

              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  right: 24,
                  top: 20,

                  color: 'white',
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>

            <DialogContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {user && user?.mfa_list?.length > 0 && user?.mfa_list?.includes('SMS_MFA') && !showSuccessMessage ? (
                <div style={{ width: '70%', textAlign: 'center' }}>
                  <Typography variant="body2" style={{ color: '#48484B', marginBottom: 24 }}>
                    You are about to disable the two-factor authentication. Are you sure?
                  </Typography>
                </div>
              ) : (
                <div style={{ width: '70%', textAlign: 'center' }}>
                  {!isSuccessEnableMFA && !isRateLimitError && !isResend && (
                    <>
                      <Typography variant="body2" style={{ color: '#48484B', marginBottom: 24 }}>
                        We’ll text a verification code to this mobile number whenever you sign in to your account.
                      </Typography>
                      <PhoneInput
                        dropdownStyle={{ position: 'fixed' }}
                        country={initialPrefix.toLowerCase()}
                        enableSearch
                        value={phoneNumber}
                        onChange={(phone, obj: CountryData) => {
                          setPhoneNumber(phone);
                          setCountryCode(obj.dialCode);
                        }}
                        isValid={!!isValidPhone}
                        inputStyle={{ width: '100%', height: 53 }}
                        specialLabel="Phone Number"
                      />
                      {isErrorEnableMFA && <PhoneErrorWrapper>Phone validation error</PhoneErrorWrapper>}
                    </>
                  )}

                  {!isValidPhone && <PhoneErrorWrapper>Invalid phone number</PhoneErrorWrapper>}
                  {((verifyNumber && !loadingEnableMFA && !showSuccessMessage) || (isResend && !showSuccessMessage)) && (
                    <>
                      <Typography variant="body2" style={{ color: '#48484B', marginBottom: 24 }}>
                        {`Enter the authentication code we sent to +${countryCode} ${phoneNumber?.substring(countryCode?.length || 0)}`}
                      </Typography>
                      <div style={{ textAlign: 'center' }}>
                        {[0, 1, 2, 3, 4, 5].map((e) => (
                          <TextField
                            key={e}
                            id={`code_${e}`}
                            onChange={(s) => {
                              const smsCodeClone = [...smsCode];
                              smsCodeClone[e] = s.target.value;
                              setSmsCode([...smsCodeClone]);
                              if (s.target.value !== '') setIdToFocus(e + 1);
                              else setIdToFocus(e);
                            }}
                            style={{ width: 38, marginRight: 10 }}
                            inputProps={{ maxLength: 1 }}
                            inputRef={(input) => input && e === idToFocus && input.focus()}
                          />
                        ))}
                      </div>
                      {isErrorEnableMFA && isRateLimitError && (
                        <PhoneErrorWrapper
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginTop: '12px',
                            alignContent: 'center',
                          }}
                        >
                          <Error color="error" style={{ marginRight: '10px' }} />
                          {`Too many attempts. Wait ${timeToReset || 'a few minutes'} before requesting a new
                          verification code.`}
                        </PhoneErrorWrapper>
                      )}

                      {isErrorMFAVerify && (
                        <PhoneErrorWrapper
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginTop: '12px',
                            alignContent: 'center',
                          }}
                        >
                          <Error color="error" style={{ marginRight: '10px' }} />
                          Invalid verification code provided, please try again.
                        </PhoneErrorWrapper>
                      )}
                    </>
                  )}
                  {((verifyNumber && !loadingEnableMFA && !showSuccessMessage) || (isResend && !showSuccessMessage)) && (
                    <Typography
                      style={{
                        color: isRateLimitError || disableResend ? theme.palette.secondary.light : theme.palette.primary.main,
                        pointerEvents: isRateLimitError || disableResend ? 'none' : 'all',
                        marginTop: '12px',
                        cursor: isRateLimitError || disableResend ? 'not-allowed' : 'pointer',
                      }}
                      variant="body2"
                      onClick={() => {
                        setIsResend(true);
                        setDisableResend(true);
                        setTimeout(() => setDisableResend(false), 10000);
                        enableMFA({
                          phone_number: `+${phoneNumber}`,
                          phone_country_code: `+${countryCode}`,
                        });
                      }}
                    >
                      Resend code
                    </Typography>
                  )}
                  {showSuccessMessage && !loadingMFAVerify && !isErrorMFAVerify && (
                    <>
                      <CheckCircle style={{ color: '#10B981' }} />
                      <Typography variant="body2" style={{ color: '#10B981' }}>
                        Succesfully enabled
                      </Typography>
                      <Typography variant="body2" style={{ color: '#48484B', marginBottom: 24 }}>
                        {`Your phone number is set to +${countryCode} ${phoneNumber?.substring(countryCode?.length || 0)}. Authentication
                  codes will be texted to this number to log in.`}
                      </Typography>
                    </>
                  )}
                </div>
              )}
            </DialogContent>

            {!showSuccessMessage && (
              <DialogActions sx={{ display: 'block', textAlign: 'center' }}>
                <ButtonComponent buttontype="cancel" onClick={handleClose} sx={{ marginRight: '18px' }} />

                <ButtonComponent
                  buttontype="mainaction"
                  text={user?.mfa_list?.length === 0 ? 'Verify' : 'Confirm'}
                  disabled={user?.mfa_list?.length === 0 && (!isValidPhone || (verifyNumber && smsCode.length < 6))}
                  onClick={async () => {
                    if (user && user?.mfa_list?.length > 0 && user?.mfa_list?.includes('SMS_MFA')) {
                      await disableMFA();
                      await triggerGetUser();
                      handleClose();
                    } else {
                      if (isValidPhone && !verifyNumber) {
                        setVerifyNumber(true);
                        if (phoneNumber && countryCode) {
                          enableMFA({
                            phone_number: phoneNumber.includes('+') ? phoneNumber : `+${phoneNumber}`,
                            phone_country_code: countryCode.includes('+') ? countryCode : `+${countryCode}`,
                          });
                        }
                      } else {
                        await verifyMFA({ code: smsCode.join('') });
                        await triggerGetUser();
                      }
                    }
                  }}
                />
              </DialogActions>
            )}
          </DialogWrapper>
        </>
      )}
      <WhiteContainer container={true}>
        <Grid item={true} xs={12} md={3} lg={3} xl={3}>
          <ContainerCard>
            <TypographyGridTitle variant="h5">Change Password</TypographyGridTitle>
          </ContainerCard>
        </Grid>
        <Grid item={true} xs={12} md={9} lg={9} xl={9}>
          <ContainerCard>
            <Formik
              initialValues={{
                newPassword: '',
                newPasswordConf: '',
                oldPassword: '',
              }}
              validateOnChange={true}
              validate={(values) => {
                const errors: FormikErrors<FormValues> = {};

                if (values.newPassword.trim().length === 0) {
                  errors.newPassword = 'Required';
                }
                if (values.oldPassword.trim().length === 0) {
                  errors.oldPassword = 'Required';
                }
                if (values.newPassword.trim().length < 7) {
                  errors.newPassword = 'Password must be at least 7 characters';
                }
                if (values.newPassword !== values.newPasswordConf) {
                  errors.newPasswordConf = 'Passwords must match';
                }

                return errors;
              }}
              onSubmit={async (values) => {
                const updateUserPasswordData: UpdateUserPasswordPayload = {
                  previous_password: values.oldPassword,
                  new_password: values.newPassword,
                };
                const response: UpdatePasswordResponse = await updateUserPassword(updateUserPasswordData);
                if (hasError(response)) {
                  setSnackBar({
                    open: true,
                    message: 'There was a problem updating your password! Please try again',
                    severity: 'error',
                  });
                  return;
                }
                setSnackBar({
                  open: true,
                  message: 'Your password was correctly updated!',
                  severity: 'success',
                });
              }}
            >
              {(formikProps) => {
                const {
                  errors: { newPassword: newPasswordError, oldPassword: oldPasswordError, newPasswordConf: newPasswordConfError },
                  touched: { newPassword: newPasswordTouched, oldPassword: oldPasswordTouched, newPasswordConf: newPasswordConfTouched },
                } = formikProps;

                return (
                  <>
                    <CardContent style={{ padding: 0 }}>
                      <FormContainer
                        onSubmit={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          formikProps.handleSubmit();
                        }}
                      >
                        <TextField
                          disabled={updateUserPasswordIsLoading}
                          variant="outlined"
                          label="Old Password"
                          type="password"
                          error={oldPasswordTouched && !!oldPasswordError}
                          helperText={oldPasswordTouched && oldPasswordError}
                          {...formikProps.getFieldProps('oldPassword')}
                        />
                        <TextField
                          disabled={updateUserPasswordIsLoading}
                          variant="outlined"
                          label="New Password"
                          type="password"
                          error={newPasswordTouched && !!newPasswordError}
                          helperText={newPasswordTouched && newPasswordError}
                          {...formikProps.getFieldProps('newPassword')}
                        />
                        <TextField
                          disabled={updateUserPasswordIsLoading}
                          variant="outlined"
                          type="password"
                          label="Password Confirmation"
                          error={newPasswordConfTouched && !!newPasswordConfError}
                          helperText={newPasswordConfTouched && newPasswordConfError}
                          {...formikProps.getFieldProps('newPasswordConf')}
                        />
                      </FormContainer>
                    </CardContent>

                    <ActionsWrapper hAlign="flex-end">
                      <ButtonComponent buttontype="cancel" onClick={() => formikProps.handleReset()} />

                      <ButtonComponent
                        buttontype="mainaction"
                        text="Save"
                        disabled={updateUserPasswordIsLoading || !!oldPasswordError || !!newPasswordError || !!newPasswordConfError}
                        onClick={() => formikProps.submitForm()}
                      />
                    </ActionsWrapper>
                  </>
                );
              }}
            </Formik>
          </ContainerCard>
        </Grid>
      </WhiteContainer>
      <SecondWhiteContainer>
        <FirstBlockContainer>
          <Typography variant="h5">Login History</Typography>
        </FirstBlockContainer>
        {loginUserInfoData && loginUserInfoData?.logins?.length > 0 ? (
          <Grid item>
            <TableCustomSize>
              <TableHead>
                <TableRow>
                  <TableCell>LOGIN</TableCell>
                  <TableCell>IP ADDRESS</TableCell>
                  <TableCell>DATE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loginUserInfoData.logins.map((user, i) => (
                  <TableRow key={i}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.ip}</TableCell>
                    <TableCell>{user.date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableCustomSize>
          </Grid>
        ) : (
          <EmptyState />
        )}
      </SecondWhiteContainer>
    </>
  );

  return securityContent;
};

export default SecurityTab;
